import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Button } from "components/Elements"
import { Bike } from "models/bike"
import { BikeRegistrationActionType, useBikeRegistrationProcess } from "features/dashboard/hooks"
import {
	bikeStatusList,
	BIKE_STATUS_IN_SALE,
	BIKE_STATUS_SOLD,
	IS_FNUCI_SYNC,
	PRIVATE_GENDER_VALUE,
	unknownBikeStatus,
} from "utils/constants"
import { privateRoutes } from "router/routes"

const SOCIAL_REASON_MAX_LENGTH = 15

export type BikeRowProps = {
	bike: Bike
}

export const BikeRow: React.FC<BikeRowProps> = ({ bike }) => {
	const { t } = useTranslation()
	const { bikeRegistrationDispatch } = useBikeRegistrationProcess()

	const navigate = useNavigate()

	const bikeIsSyncWithFnuci = bike.is_sync_with_fnuci === IS_FNUCI_SYNC

	const goToBikePage = (bike: Bike) => {
		// If bike is not sync, we redirect user to register bike page
		if (!bikeIsSyncWithFnuci) {
			bikeRegistrationDispatch({
				type: BikeRegistrationActionType.BIKE_REGISTRATION_STEP,
				bikeId: bike.bike_id,
				bikeInfo: null,
				bike: bike,
			})
		} else {
			bike?.bike_id && navigate(`${privateRoutes.bike.basePath}/${bike?.bike_id}`)
		}
	}

	const isBikeSold = bike.bike_status === BIKE_STATUS_SOLD
	const isBikeInTransit = isBikeSold && bike.bike_in_transit

	const bikeStatus =
		bikeStatusList.find(
			(status) => status.value === (isBikeInTransit ? BIKE_STATUS_IN_SALE : bike.bike_status)
		) ?? unknownBikeStatus

	const truncatedSocialReason = (socialReason: string) => {
		return socialReason.length > SOCIAL_REASON_MAX_LENGTH
			? socialReason.slice(0, SOCIAL_REASON_MAX_LENGTH) + "..."
			: socialReason
	}
	// prettier-ignore
	const displayedBikeName = bike?.operator_data?.bike_name
		? bike.operator_data.bike_name
		: t("dashboard.bike_name_prefix", { ns: "bike" }) + (bike.bike_owner.gender === PRIVATE_GENDER_VALUE
				? bike.bike_owner.first_name
				: truncatedSocialReason(bike.bike_owner.social_reason))

	return (
		<tr className="c-table__row-4 items-center">
			<td className="p-3">
				<p className={`text-${bikeStatus.color} font-semibold`}>{bikeStatus.label}</p>
			</td>
			<td className="p-3">{displayedBikeName}</td>
			<td className="p-3">{bike?.bike_id}</td>
			<td className="p-3">
				<Button
					type="button"
					onClick={() => goToBikePage(bike)}
					className="px-8 py-1"
					size="small"
				>
					{t("global.see")}
				</Button>
			</td>
		</tr>
	)
}
